import React from 'react'
import { useEffect, useState } from 'react'
import {
  Layout,
  ToastProvider,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import { useAuth } from '@praxis/component-auth'
import authService from '../services/auth'
import {
  getAssociatedVendors,
  getSavedViewsMetadata,
} from '../services/requests'
import { supportLinkUrl, TOASTER_DEFAULTS } from '../globalConfig/constants'
import { Outlet } from 'react-router-dom'
import { LoadingPage } from '../globalComponents/LoadingPage'
import { NoAssociatedVendorsPage } from '../globalComponents/NoAssociatedVendorsPage'
import { SideNavigationWrapper } from '../globalComponents/SideNavigationWrapper'
import { VendorContext } from '../globalState/Vendor'
import { SavedViewContext } from '../globalState/SavedViews'
import { Header, itemCommonUtilis } from 'item-component'
import apiConfig from '../globalConfig/apiConfig'

export const Main = () => {
  const auth = useAuth()
  const makeToast = useToaster()
  const { isAuthenticated, session } = auth
  const [tokenExpirationTimeout, setTokenExpirationTimeout] = useState()
  const [associatedVendorsResponse, setAssociatedVendorsResponse] = useState()
  const [state, dispatch] = React.useContext(VendorContext)
  // eslint-disable-next-line no-unused-vars
  const [savedViewState, savedViewDispatch] = React.useContext(SavedViewContext)

  const baseLogoutUrl = apiConfig?.auth?.logoutUrl
  const logoutUrlParams = new URLSearchParams()
  logoutUrlParams.set('target', window.location.origin)
  const logoutUrl = new URL(baseLogoutUrl)
  logoutUrl.search = logoutUrlParams.toString()

  useEffect(() => {
    if (state?.selectedVendor) {
      getSavedViewsMetadata(parseInt(state?.selectedVendor))
        .then((response) => {
          if (response?.status === 200) {
            savedViewDispatch({
              type: 'update_saved_views',
              savedViews: response?.data,
            })
          }
        })
        .catch(() => {
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'error',
            heading: 'Error',
            message: 'Failed to retrieve Saved Views',
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedViewDispatch, state?.selectedVendor])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (session?.access?.exp * 1000 < new Date().getTime()) {
        window.location.href = logoutUrl.href
      }
      if (session) {
        const { lanId } = session.userInfo
        authService.isSSOSessionActive(lanId, window.location.hostname)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  useEffect(() => {
    getAssociatedVendors()
      .then((response) => {
        if (response?.status === 200) {
          setAssociatedVendorsResponse(response)
          dispatch({ type: 'update_vendors', vendors: response.data })
        } else {
          setAssociatedVendorsResponse(response)
          dispatch({ type: 'update_vendors', vendors: [] })
        }
      })
      .catch((e) => {
        setAssociatedVendorsResponse(e?.response)
        dispatch({ type: 'update_vendors', vendors: [] })
      })
  }, [session?.userInfo?.lanId, dispatch])

  useEffect(() => {
    if (session?.access?.exp * 1000 < new Date().getTime()) {
      window.location.href = logoutUrl.href
    }
    if (tokenExpirationTimeout && session?.access?.exp) {
      clearTimeout(tokenExpirationTimeout)
    }
    if (session?.access?.exp) {
      setTokenExpirationTimeout(
        setTimeout(
          () => {
            window.location.href = logoutUrl.href
          },
          session?.access?.exp * 1000 - new Date().getTime(),
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access?.exp])

  const handleVendorSelection = (vendorId) => {
    dispatch({
      type: 'update_selected_vendor',
      selectedVendor: vendorId,
    })
  }

  const getVendorOptions = () => {
    if (state?.vendors) {
      const returnArray = []
      Object.keys(state.vendors).forEach((vendorKey) => {
        returnArray.push({
          value: vendorKey,
          label: `${state.vendors[vendorKey]} - ${vendorKey}`,
          id: vendorKey,
        })
      })
      return returnArray
    } else {
      return []
    }
  }

  if (isAuthenticated()) {
    return (
      <Layout
        className="hesp-header"
        theme="default"
        darkMode="system"
        data-testid="mainAuthenticatedLayout"
      >
        <Header
          appTitle={itemCommonUtilis?.isItemHub ? 'ItemHub' : 'Cost Management'}
          userRole="User Role"
          useAuth={useAuth}
          additionalSelectorDetails={{
            options: getVendorOptions(),
            onSelect: handleVendorSelection,
            label: 'Representing Vendor:',
            value: state?.selectedVendor,
          }}
          isSupportVisible
          supportUrl={supportLinkUrl}
        />
        <ToastProvider location="top">
          <SideNavigationWrapper />
          {state.vendors ? (
            Object.keys(state.vendors)?.length ? (
              <Outlet />
            ) : (
              <NoAssociatedVendorsPage
                userId={session?.userInfo?.lanId}
                associatedVendorsResponse={associatedVendorsResponse}
              />
            )
          ) : (
            <LoadingPage />
          )}
        </ToastProvider>
      </Layout>
    )
  } else {
    auth.login({ redirect: window.location.href })
  }
}
